import axios from 'axios';
import { ApiOptions, Maybe, OrgId, PagedResult, Site } from '@csp/csp-common-model';
import { toRestOptions } from '@csp/csp-fe-auth';
import { BinaryFileData, StudyDocsRestServiceV1 } from '@csp/dmdp-api-client';
import {
  StudyFileCriteriaFieldsV1,
  StudyFileInV1,
  StudyFileQueryOperatorV1,
  StudyFileQuerySortOrderV1,
  StudyFileQueryTypeV1,
  StudyFileSetClassificationInV1,
  StudyFileSetDomainsInV1,
  StudyFileSetOrgIdsInV1,
  StudyFileSetRolesInV1,
  StudyFileSortFieldsV1,
  StudyFileV1,
  StudyFilesV1,
} from '@csp/dmdp-api-file-storage-studydocs-dto';
import { Page } from '@csp/dmdp-api-common-dto';
import { CachedUserQueryService, UserQuery } from '@csp/csp-common-user-query';
import { OrgService } from '@csp/csp-common-org';
import { PagedResultUtil } from '@csp/csp-common-util';
import { StudyDocumentsFileDetails } from '../model/StudyDocumentsFileDetails';
import { StudyDocumentsFileMapper } from '../mapper/StudyDocumentsFileMapper';
import { StudyDocumentsFile } from '../model/StudyDocumentsFile';
import { StudyDocumentsFolder } from '../model/StudyDocumentsFolder';
import { PagedStudyDocumentsFilesMapper } from '../mapper/PagedStudyDocumentsFilesMapper';
import { StudyDocumentsQueryFilter } from '../model/StudyDocumentsQueryFilter';
import { StudyDocumentsQueryMapper } from '../mapper/StudyDocumentsQueryMapper';
import { Domain } from '../model/Domain';

const getFolders = async (apiOptions?: ApiOptions): Promise<StudyDocumentsFolder[]> => {
  const { studyDocsTaxonomy } = await StudyDocsRestServiceV1.getTaxonomy(toRestOptions(apiOptions));

  const folders = studyDocsTaxonomy.map(({ studyDocsTaxonId, eventId, name }) => ({
    id: studyDocsTaxonId,
    eventId,
    name,
  }));

  return folders;
};

const getFiles = async (
  filter: StudyDocumentsQueryFilter,
  sites: Site[],
  folderId: Maybe<string>,
  next?: string,
  apiOptions?: ApiOptions,
): Promise<PagedResult<StudyDocumentsFile>> => {
  const query = StudyDocumentsQueryMapper.byFilter(filter, sites, [
    {
      fieldCriterion: {
        field: StudyFileCriteriaFieldsV1.TRASH,
        type: StudyFileQueryTypeV1.EQ,
        values: [false],
      },
    },
    ...(folderId
      ? [
          {
            fieldCriterion: {
              field: StudyFileCriteriaFieldsV1.CLASSIFICATION,
              type: StudyFileQueryTypeV1.IN,
              values: [folderId],
            },
          },
        ]
      : []),
  ]);

  const result = await StudyDocsRestServiceV1.queryStudyFiles(toRestOptions(apiOptions), query, {
    count: !!filter.count,
    limit: filter.pageSize,
    next,
  });

  return PagedStudyDocumentsFilesMapper.from(result);
};

const getAllFilesForDomain = async (
  domain: Domain,
  orgIds?: OrgId[],
  apiOptions?: ApiOptions,
): Promise<StudyDocumentsFile[]> => {
  const getPage = async (page: Page): Promise<StudyFilesV1> =>
    StudyDocsRestServiceV1.queryStudyFiles(
      toRestOptions(apiOptions),
      {
        criterion: {
          operator: StudyFileQueryOperatorV1.AND,
          criteria: [
            {
              fieldCriterion: {
                field: StudyFileCriteriaFieldsV1.TRASH,
                type: StudyFileQueryTypeV1.EQ,
                values: [false],
              },
            },
            {
              fieldCriterion: {
                field: StudyFileCriteriaFieldsV1.DOMAINS,
                type: StudyFileQueryTypeV1.IN,
                values: [domain],
              },
            },
            ...(orgIds
              ? [
                  {
                    fieldCriterion: {
                      field: StudyFileCriteriaFieldsV1.ORG_IDS,
                      type: StudyFileQueryTypeV1.IN,
                      values: orgIds,
                    },
                  },
                ]
              : []),
          ],
        },
        sort: [
          {
            field: StudyFileSortFieldsV1.CREATED_AT,
            order: StudyFileQuerySortOrderV1.DESC,
          },
        ],
      },
      page,
    );
  return PagedResultUtil.getAllItems(getPage, 'studyFiles', StudyDocumentsFileMapper.from);
};

const getAllUserResources = async (apiOptions?: ApiOptions): Promise<StudyDocumentsFile[]> =>
  getAllFilesForDomain(Domain.USER_RESOURCES, undefined, apiOptions);

const getTrashedFiles = async (
  filter: StudyDocumentsQueryFilter,
  sites: Site[],
  next?: string,
  apiOptions?: ApiOptions,
): Promise<PagedResult<StudyDocumentsFile>> => {
  const query = StudyDocumentsQueryMapper.byFilter(filter, sites, [
    {
      fieldCriterion: {
        field: StudyFileCriteriaFieldsV1.TRASH,
        type: StudyFileQueryTypeV1.EQ,
        values: [true],
      },
    },
  ]);

  const result = await StudyDocsRestServiceV1.queryStudyFiles(toRestOptions(apiOptions), query, {
    count: !!filter.count,
    limit: filter.pageSize,
    next,
  });

  return { ...result.paging, data: result.studyFiles.map(StudyDocumentsFileMapper.from) };
};

const getTrashedFilesByIds = async (fileIds: string[], apiOptions?: ApiOptions): Promise<StudyDocumentsFile[]> => {
  const getPage = async (page: Page): Promise<StudyFilesV1> =>
    StudyDocsRestServiceV1.queryStudyFiles(
      toRestOptions(apiOptions),
      {
        criterion: {
          operator: StudyFileQueryOperatorV1.AND,
          criteria: [
            {
              fieldCriterion: {
                field: StudyFileCriteriaFieldsV1.STUDY_FILE_ID,
                type: StudyFileQueryTypeV1.IN,
                values: fileIds,
              },
            },
            {
              fieldCriterion: {
                field: StudyFileCriteriaFieldsV1.TRASH,
                type: StudyFileQueryTypeV1.EQ,
                values: [true],
              },
            },
          ],
        },
      },
      page,
    );

  return PagedResultUtil.getAllItems(getPage, 'studyFiles', StudyDocumentsFileMapper.from);
};

const trashFile = async (fileId: string, apiOptions?: ApiOptions): Promise<void> => {
  await StudyDocsRestServiceV1.putFileTrashStatus(toRestOptions(apiOptions), fileId, true);
};

const restoreFile = async (fileId: string, apiOptions?: ApiOptions): Promise<void> => {
  await StudyDocsRestServiceV1.putFileTrashStatus(toRestOptions(apiOptions), fileId, false);
};

const getFolder = async (folderId: string, apiOptions?: ApiOptions): Promise<StudyDocumentsFolder> => {
  const { name, eventId, studyDocsTaxonId } = await StudyDocsRestServiceV1.getTaxon(
    toRestOptions(apiOptions),
    folderId,
  );

  return { id: studyDocsTaxonId, eventId, name };
};

// Todo: Can this be fetched in a more performant way?
const getFoldersByIds = async (folderIds: string[], apiOptions?: ApiOptions): Promise<StudyDocumentsFolder[]> =>
  await Promise.all(folderIds.map(async folderId => await getFolder(folderId, apiOptions)));

const createFolder = async (folderName: string, apiOptions?: ApiOptions): Promise<StudyDocumentsFolder> => {
  const { name, eventId, studyDocsTaxonId } = await StudyDocsRestServiceV1.createTaxonomy(toRestOptions(apiOptions), {
    name: folderName,
  });

  return { id: studyDocsTaxonId, eventId, name };
};

const editFolder = async (folder: StudyDocumentsFolder, apiOptions?: ApiOptions): Promise<StudyDocumentsFolder> => {
  const { name, eventId, studyDocsTaxonId } = await StudyDocsRestServiceV1.editTaxonomy(
    toRestOptions(apiOptions),
    folder.id,
    {
      name: folder.name,
      eventId: folder.eventId,
    },
  );

  return { id: studyDocsTaxonId, eventId, name };
};

const getFileDetails = async (fileId: string, apiOptions?: ApiOptions): Promise<StudyDocumentsFileDetails> => {
  const studyFileV1 = await StudyDocsRestServiceV1.getStudyFile(toRestOptions(apiOptions), fileId);

  const [maybeUser, orgs, folders] = await Promise.all([
    CachedUserQueryService.byUserId(studyFileV1.audit.createdBy, UserQuery.defaultFields, apiOptions).catch(
      () => undefined,
    ),
    OrgService.fetchOrgsByIds(studyFileV1.orgIds, apiOptions),
    getFoldersByIds(studyFileV1.classification, apiOptions),
  ]);

  return StudyDocumentsFileMapper.toStudyDocumentsFileDetails(studyFileV1, maybeUser, orgs, folders);
};

const deleteFile = async (fileId: string, apiOptions?: ApiOptions): Promise<void> => {
  await StudyDocsRestServiceV1.deleteFile(toRestOptions(apiOptions), fileId);
};

const getBinaryFileData = async (fileId: string, apiOptions?: ApiOptions): Promise<BinaryFileData> =>
  StudyDocsRestServiceV1.getBinaryFileData(toRestOptions(apiOptions), fileId);

const getFileUrl = async (fileId: string, apiOptions?: ApiOptions): Promise<string> =>
  StudyDocsRestServiceV1.getFileUrl(toRestOptions(apiOptions), fileId);

const deleteFolder = async (folderId: string, apiOptions?: ApiOptions): Promise<void> => {
  await StudyDocsRestServiceV1.deleteTaxonomy(toRestOptions(apiOptions), folderId);
};

const createStudyFile = async (
  fields: StudyFileInV1,
  apiOptions?: ApiOptions,
): Promise<{ signedUrl: Maybe<string>; fileId: string }> => {
  const { signedUrl, data } = await StudyDocsRestServiceV1.createStudyFile(toRestOptions(apiOptions), fields);

  return { signedUrl, fileId: data.studyFileId };
};

const uploadStudyFile = async (
  file: unknown,
  mimeType: string,
  signedUrl: string,
  apiOptions?: ApiOptions,
): Promise<void> =>
  // This request is towards S3
  // We need to use a raw axios instance, it fails if any additional headers are present
  await StudyDocsRestServiceV1.uploadStudyFile(toRestOptions(apiOptions, axios.create()), {
    file,
    mimeType,
    signedUrl,
  });

const updateFolders = async (
  args: StudyFileSetClassificationInV1 & { fileId: string },
  apiOptions?: ApiOptions,
): Promise<StudyFileV1> => await StudyDocsRestServiceV1.editClassification(toRestOptions(apiOptions), args);

const updateDomains = async (
  args: StudyFileSetDomainsInV1 & { fileId: string },
  apiOptions?: ApiOptions,
): Promise<StudyFileV1> => await StudyDocsRestServiceV1.editDomains(toRestOptions(apiOptions), args);

const updateOrgIds = async (
  args: StudyFileSetOrgIdsInV1 & { fileId: string },
  apiOptions?: ApiOptions,
): Promise<StudyFileV1> => await StudyDocsRestServiceV1.editOrgIds(toRestOptions(apiOptions), args);

const updateRoles = async (
  args: StudyFileSetRolesInV1 & { fileId: string },
  apiOptions?: ApiOptions,
): Promise<StudyFileV1> => await StudyDocsRestServiceV1.editRoles(toRestOptions(apiOptions), args);

export const StudyDocumentsService = {
  getFolders,
  getFileDetails,
  getFiles,
  getAllFilesForDomain,
  getAllUserResources,
  getTrashedFiles,
  getTrashedFilesByIds,
  trashFile,
  restoreFile,
  getFolder,
  createFolder,
  deleteFile,
  editFolder,
  getBinaryFileData,
  getFileUrl,
  deleteFolder,
  createStudyFile,
  uploadStudyFile,
  updateFolders,
  updateDomains,
  updateOrgIds,
  updateRoles,
};
